import React, { useState, useRef } from 'react';
import { 
  Car, 
  FileText, 
  Activity, 
  Image as ImageIcon, 
  User, 
  ArrowRight, 
  ArrowLeft, 
  Upload, 
  X, 
  CheckCircle, 
  AlertCircle, 
  Loader2 
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function StepForm() {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Din bil
    nummerplade: '',
    brand: '',
    model: '',
    // Step 2: Leasingaftale
    leasingudbyder: '',
    restvaerdi: '',
    udloebsdato: '',
    // Step 3: Bilens stand
    km: '',
    skader: 'Ingen skader',
    service: 'Ja, fuld servicebog',
    // Step 5: Kontaktoplysninger
    navn: '',
    telefon: '',
    email: '',
    // Honeypot
    website_url: '',
  });

  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [submissionId, setSubmissionId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fileInputRef = useRef(null);

  const steps = [
    { id: 1, title: t('form.step1_title'), icon: Car },
    { id: 2, title: t('form.step2_title'), icon: FileText },
    { id: 3, title: t('form.step3_title'), icon: Activity },
    { id: 4, title: t('form.step4_title'), icon: ImageIcon },
    { id: 5, title: t('form.step5_title'), icon: User },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // File Upload Handling
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    setFileError('');
    
    // Filter out non-allowed types
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    const validTypesFiles = newFiles.filter(file => allowedTypes.includes(file.type));
    
    if (validTypesFiles.length !== newFiles.length) {
      setFileError('Kun PDF, PNG, JPEG, GIF og WEBP er tilladt.');
      return;
    }

    const updatedFiles = [...files, ...validTypesFiles];

    // Enforce max 5 files
    if (updatedFiles.length > 5) {
      setFileError('Du kan højst uploade 5 billeder i alt.');
      return;
    }

    // Enforce max 10MB per file
    const fileTooLarge = validTypesFiles.some(file => file.size > 10 * 1024 * 1024);
    if (fileTooLarge) {
      setFileError('Enkeltfiler må ikke overstige 10 MB.');
      return;
    }

    // Enforce max 20MB aggregate
    const totalSize = updatedFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > 20 * 1024 * 1024) {
      setFileError('Den samlede filstørrelse må ikke overstige 20 MB.');
      return;
    }

    setFiles(updatedFiles);
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
    setFileError('');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  // Validation per step
  const isStepValid = () => {
    if (currentStep === 1) {
      return formData.nummerplade.trim() !== '' && formData.brand.trim() !== '' && formData.model.trim() !== '';
    }
    if (currentStep === 2) {
      return formData.leasingudbyder.trim() !== '' && formData.restvaerdi.trim() !== '' && formData.udloebsdato.trim() !== '';
    }
    if (currentStep === 3) {
      return formData.km.trim() !== '' && formData.skader !== '' && formData.service !== '';
    }
    if (currentStep === 4) {
      // Images are optional but recommended, so we allow proceeding even with 0 files
      return true;
    }
    if (currentStep === 5) {
      return formData.navn.trim() !== '' && formData.telefon.trim() !== '' && formData.email.trim() !== '';
    }
    return true;
  };

  const nextStep = () => {
    if (isStepValid() && currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isStepValid()) return;

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const data = new FormData();
      
      // Append honeypot
      data.append('website_url', formData.website_url);
      
      // Append form fields
      data.append('nummerplade', formData.nummerplade);
      data.append('brand', formData.brand);
      data.append('model', formData.model);
      data.append('leasingudbyder', formData.leasingudbyder);
      data.append('restvaerdi', formData.restvaerdi);
      data.append('udloebsdato', formData.udloebsdato);
      data.append('km', formData.km);
      data.append('skader', formData.skader);
      data.append('service', formData.service);
      data.append('navn', formData.navn);
      data.append('telefon', formData.telefon);
      data.append('email', formData.email);

      // Append files
      files.forEach((file) => {
        data.append('billeder', file);
      });

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: data,
        // Let the browser set Content-Type with boundary for multipart/form-data
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setSubmitStatus('success');
        setSubmissionId(result.submissionId);
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.message || 'Der opstod en fejl under indsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nummerplade: '',
      brand: '',
      model: '',
      leasingudbyder: '',
      restvaerdi: '',
      udloebsdato: '',
      km: '',
      skader: 'Ingen skader',
      service: 'Ja, fuld servicebog',
      navn: '',
      telefon: '',
      email: '',
      website_url: '',
    });
    setFiles([]);
    setCurrentStep(1);
    setSubmitStatus(null);
    setSubmissionId('');
    setErrorMessage('');
  };

  return (
    <section id="vurdering-form" className="py-20 bg-slate-50 border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-0 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
          
          {/* Left Column: Form and titles */}
          <div className="lg:col-span-7 flex flex-col justify-center px-4 sm:px-0">
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-black italic text-[#293e30] tracking-tight mb-3 uppercase">
                Få et uforpligtende tilbud nu
              </h2>
              <p className="text-slate-600">
                Udfyld vores intelligente, trinvise formular på under 2 minutter. Det er gratis og 100% uforpligtende.
              </p>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-none border border-slate-200 shadow-2xl shadow-slate-300/60 overflow-hidden">
          {submitStatus === 'success' ? (
            /* Success State */
            <div className="p-8 md:p-12 text-center animate-in fade-in zoom-in-95 duration-300">
              <div className="h-16 w-16 bg-[#76eeda]/10 rounded-full flex items-center justify-center text-[#293e30] mx-auto mb-6">
                <CheckCircle className="h-10 w-10" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                Tak for din forespørgsel!
              </h3>
              <p className="text-slate-600 max-w-md mx-auto mb-8 leading-relaxed">
                Vi har modtaget dine oplysninger og billeder. Vores eksperter går straks i gang med at vurdere din bil. Du modtager et konkret tilbud indenfor 24 timer.
              </p>
              
              <div className="bg-slate-50 rounded-none p-5 max-w-md mx-auto text-left border border-slate-100 mb-8">
                <div className="flex justify-between text-xs text-slate-500 mb-2">
                  <span>Sagsnummer:</span>
                  <span className="font-mono font-semibold text-slate-700">{submissionId || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Estimeret svartid:</span>
                  <span className="font-semibold text-[#293e30]">Indenfor 24 timer (hverdage)</span>
                </div>
              </div>

              <button
                onClick={resetForm}
                className="inline-flex items-center justify-center px-6 py-3 rounded-none bg-[#76eeda] hover:bg-[#5cdbc7] text-[#293e30] font-bold text-sm transition-colors cursor-pointer"
              >
                Indsend en ny forespørgsel
              </button>
            </div>
          ) : (
            /* Active Form State */
            <form onSubmit={handleSubmit} className="p-6 md:p-10">
              {/* Honeypot field (STRICTLY HIDDEN FROM HUMANS) */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Honeypot</label>
                <input
                  id="website_url"
                  type="text"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {/* Progress Bar */}
              <div className="mb-10">
                <div className="flex items-center justify-between relative">
                  {/* Background Line */}
                  <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-0.5 bg-slate-100 z-0" />
                  
                  {/* Active Progress Line */}
                  <div 
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-slate-900 transition-all duration-300 z-0"
                    style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
                  />

                  {steps.map((step) => {
                    const StepIcon = step.icon;
                    const isActive = currentStep >= step.id;
                    const isCurrent = currentStep === step.id;

                    return (
                      <div key={step.id} className="relative z-10 flex flex-col items-center">
                        <button
                          type="button"
                          disabled={step.id > currentStep && !isStepValid()}
                          onClick={() => {
                            // Only allow clicking past steps or the next step if valid
                            if (step.id < currentStep || (step.id === currentStep + 1 && isStepValid())) {
                              setCurrentStep(step.id);
                            }
                          }}
                          className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                            isCurrent
                              ? 'bg-slate-900 text-white scale-110'
                              : isActive
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-100 text-slate-400'
                          }`}
                        >
                          {step.id}
                        </button>
                        <span className={`hidden sm:block text-[11px] font-semibold mt-2 transition-colors duration-300 ${
                          isCurrent ? 'text-slate-900 font-bold' : isActive ? 'text-slate-600' : 'text-slate-400'
                        }`}>
                          {step.title}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Step Title & Icon */}
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4 mb-8">
                <div>
                  <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">{t('form.step')} {currentStep} {t('form.of')} 5</span>
                  <h3 className="text-lg font-bold text-slate-900 leading-tight">{steps[currentStep - 1].title}</h3>
                </div>
              </div>

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm flex items-start gap-2.5">
                  <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">{t('form.error_title')}</p>
                    <p className="text-rose-600/90">{t('form.error_desc')}</p>
                  </div>
                </div>
              )}

              {/* Step Content */}
              <div className="min-h-[240px] animate-fade-in">
                {currentStep === 1 && (
                  /* Step 1: Din bil */
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.license_plate')}</label>
                      <input
                        type="text"
                        name="nummerplade"
                        value={formData.nummerplade}
                        onChange={handleInputChange}
                        placeholder={t('form.license_plate_placeholder')}
                        className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.brand')} *</label>
                        <input
                          type="text"
                          name="brand"
                          value={formData.brand}
                          onChange={handleInputChange}
                          placeholder={t('form.brand_placeholder')}
                          required
                          className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.model')} *</label>
                        <input
                          type="text"
                          name="model"
                          value={formData.model}
                          onChange={handleInputChange}
                          placeholder={t('form.model_placeholder')}
                          required
                          className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  /* Step 2: Leasing & Finansiering */
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.leasing_provider')} *</label>
                      <input
                        type="text"
                        name="leasingudbyder"
                        value={formData.leasingudbyder}
                        onChange={handleInputChange}
                        placeholder={t('form.leasing_provider_placeholder')}
                        required
                        className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.residual_value')} *</label>
                        <input
                          type="number"
                          name="restvaerdi"
                          value={formData.restvaerdi}
                          onChange={handleInputChange}
                          placeholder={t('form.residual_value_placeholder')}
                          required
                          className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.expiry_date')} *</label>
                        <input
                          type="date"
                          name="udloebsdato"
                          value={formData.udloebsdato}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all text-slate-900 font-medium"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  /* Step 3: Bilens stand */
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.mileage')} *</label>
                      <input
                        type="number"
                        name="km"
                        value={formData.km}
                        onChange={handleInputChange}
                        placeholder={t('form.mileage_placeholder')}
                        required
                        className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">{t('form.damages')} *</label>
                        <div className="space-y-2">
                          {[
                            { value: 'Ingen skader', label: t('form.damages_none') },
                            { value: 'Mindre ridser/brugsspor', label: t('form.damages_minor') },
                            { value: 'Større skader', label: t('form.damages_major') }
                          ].map((option) => (
                            <label
                              key={option.value}
                              className={`flex items-center gap-3 px-4 py-3 rounded-none border cursor-pointer transition-all ${
                                formData.skader === option.value
                                  ? 'border-slate-900 bg-slate-50/50 font-semibold text-slate-900'
                                  : 'border-slate-200 text-slate-600 hover:bg-slate-50/30'
                              }`}
                            >
                              <input
                                type="radio"
                                name="skader"
                                value={option.value}
                                checked={formData.skader === option.value}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-slate-900 border-slate-300 focus:ring-slate-900"
                              />
                              <span className="text-sm">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">{t('form.service_history')} *</label>
                        <div className="space-y-2">
                          {[
                            { value: 'Ja, fuld servicebog', label: t('form.service_full') },
                            { value: 'Nej, mangler service', label: t('form.service_none') }
                          ].map((option) => (
                            <label
                              key={option.value}
                              className={`flex items-center gap-3 px-4 py-3 rounded-none border cursor-pointer transition-all ${
                                formData.service === option.value
                                  ? 'border-slate-900 bg-slate-50/50 font-semibold text-slate-900'
                                  : 'border-slate-200 text-slate-600 hover:bg-slate-50/30'
                              }`}
                            >
                              <input
                                type="radio"
                                name="service"
                                value={option.value}
                                checked={formData.service === option.value}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-slate-900 border-slate-300 focus:ring-slate-900"
                              />
                              <span className="text-sm">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 4 && (
                  /* Step 4: Upload billeder */
                  <div className="space-y-5">
                    <div
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-200 hover:border-slate-400 rounded-none p-8 text-center cursor-pointer transition-all bg-slate-50/50 hover:bg-slate-50 flex flex-col items-center justify-center group"
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                        className="hidden"
                      />
                      <div className="h-12 w-12 rounded-full bg-white shadow-sm flex items-center justify-center text-slate-500 group-hover:scale-105 transition-transform mb-4">
                        <Upload className="h-6 w-6" />
                      </div>
                      <p className="text-sm font-bold text-slate-800 mb-1">
                        {t('form.upload_drag')} {t('form.upload_browse')}
                      </p>
                      <p className="text-xs text-slate-500 max-w-xs mx-auto">
                        {t('form.upload_limits')}
                      </p>
                    </div>

                    {fileError && (
                      <div className="p-3 rounded-none bg-rose-50 border border-rose-100 text-rose-700 text-xs flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span>{fileError}</span>
                      </div>
                    )}

                    {/* Selected Files List */}
                    {files.length > 0 && (
                      <div className="space-y-2">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                          {t('form.upload_count')} ({files.length} {t('form.of')} 5)
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {files.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 rounded-none border border-slate-100 bg-slate-50/30 text-xs"
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                {file.type.startsWith('image/') ? (
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt="Preview"
                                    className="h-8 w-8 rounded-none object-cover shrink-0"
                                  />
                                ) : (
                                  <div className="h-8 w-8 rounded-none bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
                                    <FileText className="h-4 w-4" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="font-semibold text-slate-800 truncate">{file.name}</p>
                                  <p className="text-slate-400">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFile(index);
                                }}
                                className="p-1 rounded-none hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 5 && (
                  /* Step 5: Kontaktoplysninger */
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.name')} *</label>
                      <input
                        type="text"
                        name="navn"
                        value={formData.navn}
                        onChange={handleInputChange}
                        placeholder={t('form.name_placeholder')}
                        required
                        className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.phone')} *</label>
                        <input
                          type="tel"
                          name="telefon"
                          value={formData.telefon}
                          onChange={handleInputChange}
                          placeholder={t('form.phone_placeholder')}
                          required
                          className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('form.email')} *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder={t('form.email_placeholder')}
                          required
                          className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between border-t border-slate-100 pt-6 mt-8">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-none border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-sm transition-all cursor-pointer"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('form.prev')}
                  </button>
                ) : (
                  <div />
                )}

                {currentStep < 5 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!isStepValid()}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-none bg-black hover:bg-slate-900 text-white font-bold text-sm shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('form.next')}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting || !isStepValid()}
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-none bg-black hover:bg-slate-900 text-white font-bold text-sm shadow-md transition-all hover:-translate-y-0.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('form.submitting')}
                      </>
                    ) : (
                      <>
                        {t('form.submit')}
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

          {/* Right Column: Image (col-5) */}
          <div className="hidden lg:block lg:col-span-5 relative min-h-[600px]">
            <img 
              src="https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/636e989a-1208-40f5-bec5-d8ba5b8e927f.jpg" 
              alt="Premium bil vurdering" 
              className="absolute inset-0 w-full h-full object-cover rounded-none"
            />
            {/* Subtle premium overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
          </div>

        </div>
      </div>
    </section>
  );
}
